import React, { createContext, useContext, useState, useEffect } from 'react';
import { Web3 } from 'web3';
import ERC20_ABI from './abi/Erc20Abi.json';

const GlobalStateContext = createContext();

export const useGlobalState = () => {
  const context = useContext(GlobalStateContext);
  if (!context) {
    throw new Error('useGlobalState must be used within a GlobalStateProvider');
  }
  return context;
};

export const GlobalStateProvider = ({ children }) => {
  const [params, setParams] = useState({
    contractAddress: '',
    fromTokenAddress: '',
    toTokenAddress: '',
    poolAddress: '',
    sender: ''
  });

  // RPC节点选择状态
  const [rpcNode, setRpcNode] = useState('auto');

  const [authorizationStatus, setAuthorizationStatus] = useState({
    isAuthorized: false,
    allowance: '0',
    lastChecked: null,
    error: null
  });

  const [isCheckingAuth, setIsCheckingAuth] = useState(false);

  // Web3 instance for BSC
  const web3 = new Web3('https://bsc-dataseed.bnbchain.org');

  // Function to check ERC20 authorization
  const checkAuthorization = async () => {
    const { sender, fromTokenAddress, contractAddress } = params;
    
    // Only check if we have all required parameters
    if (!sender || !fromTokenAddress || !contractAddress) {
      setAuthorizationStatus(prev => ({
        ...prev,
        isAuthorized: false,
        allowance: '0',
        lastChecked: new Date(),
        error: null
      }));
      return;
    }

    try {
      setIsCheckingAuth(true);
      
      // Create contract instance
      const tokenContract = new web3.eth.Contract(ERC20_ABI, fromTokenAddress);
      
      // Check allowance
      const allowance = await tokenContract.methods.allowance(sender, contractAddress).call();
      
      // Consider authorized if allowance > 0
      const isAuthorized = BigInt(allowance) > 0n;
      setAuthorizationStatus({
        isAuthorized,
        allowance: allowance.toString(),
        lastChecked: new Date(),
        error: null
      });

  
    } catch (error) {
      console.error('Authorization check failed:', error);
      setAuthorizationStatus(prev => ({
        ...prev,
        isAuthorized: false,
        allowance: '0',
        lastChecked: new Date(),
        error: error.message
      }));
    } finally {
      setIsCheckingAuth(false);
    }
  };

  // Load params from localStorage on mount
  useEffect(() => {
    try {
      const savedParams = localStorage.getItem('swapParams');
      if (savedParams) {
        const parsedParams = JSON.parse(savedParams);
        setParams(parsedParams);
        console.log('从localStorage加载参数:', parsedParams);
      }
    } catch (error) {
      console.error('Failed to load params from localStorage:', error);
    }
  }, []);

  // Load rpcNode from localStorage on mount
  useEffect(() => {
    try {
      const savedRpcNode = localStorage.getItem('rpcNode');
      if (savedRpcNode) {
        setRpcNode(savedRpcNode);
        console.log('从localStorage加载RPC节点:', savedRpcNode);
      }
    } catch (error) {
      console.error('Failed to load rpcNode from localStorage:', error);
    }
  }, []);

  // Save params to localStorage when they change
  useEffect(() => {
    try {
      localStorage.setItem('swapParams', JSON.stringify(params));
    } catch (error) {
      console.error('Failed to save params to localStorage:', error);
    }
  }, [params]);

  // Save rpcNode to localStorage when it changes
  useEffect(() => {
    try {
      localStorage.setItem('rpcNode', rpcNode);
      console.log('RPC节点已保存到localStorage:', rpcNode);
    } catch (error) {
      console.error('Failed to save rpcNode to localStorage:', error);
    }
  }, [rpcNode]);

  // Set up interval to check authorization every 10 seconds
  useEffect(() => {
    // Initial check
    checkAuthorization();

    // Set up interval
    const interval = setInterval(checkAuthorization, 10000); // 10 seconds

    return () => clearInterval(interval);
  }, [params.sender, params.fromTokenAddress, params.contractAddress]);

  const updateParams = (newParams) => {
    setParams(prev => ({ ...prev, ...newParams }));
  };

  const updateRpcNode = (newRpcNode) => {
    setRpcNode(newRpcNode);
  };

  const value = {
    params,
    setParams,
    updateParams,
    rpcNode,
    setRpcNode,
    updateRpcNode,
    authorizationStatus,
    isCheckingAuth,
    checkAuthorization
  };

  return (
    <GlobalStateContext.Provider value={value}>
      {children}
    </GlobalStateContext.Provider>
  );
};

export default GlobalStateContext; 